<template>
  <Teleport :to="progressHolder" v-if="progressHolder && !isMobile">
    <div
      v-for="marker in markers"
      :key="marker.bucketIndex"
      class="reaction-timeline-marker"
      :style="{ left: `${marker.leftPercent}%` }"
      @mouseleave="closeBreakdown(marker.bucketIndex)"
    >
      <div class="reaction-timeline-marker__pill">
        <button
          type="button"
          class="reaction-timeline-marker__summary"
          :data-testid="`reaction-timeline-marker-${marker.bucketIndex}`"
          :title="`Jump to ${marker.reactions.map(reaction => reaction.emoji).join(' ')} reaction`"
          @mousedown.stop
          @touchstart.stop
          @click.stop="seekTo(marker.seekTime)"
        >
          <span class="reaction-timeline-marker__reaction">
            <span class="reaction-timeline-marker__emoji">{{ marker.reactions[0].emoji }}</span>
            <span class="reaction-timeline-marker__count">{{ marker.reactions.length > 1 ? totalCount(marker) : marker.reactions[0].count }}</span>
          </span>
        </button>

        <button
          v-if="marker.reactions.length > 1"
          type="button"
          class="reaction-timeline-marker__toggle"
          :class="{ 'reaction-timeline-marker__toggle--open': expandedBucketIndex === marker.bucketIndex }"
          aria-label="Show all reactions at this point"
          :aria-expanded="expandedBucketIndex === marker.bucketIndex"
          :data-testid="`reaction-timeline-marker-toggle-${marker.bucketIndex}`"
          @mousedown.stop
          @touchstart.stop
          @click.stop="toggleBreakdown(marker.bucketIndex)"
        >
          <Icon name="general.chevronDown" size="xs" />
        </button>
      </div>

      <div
        v-if="marker.reactions.length > 1 && expandedBucketIndex === marker.bucketIndex"
        class="reaction-timeline-marker__breakdown"
        :data-testid="`reaction-timeline-marker-breakdown-${marker.bucketIndex}`"
      >
        <span v-for="reaction in marker.reactions" :key="reaction.emoji" class="reaction-timeline-marker__reaction">
          <span class="reaction-timeline-marker__emoji">{{ reaction.emoji }}</span>
          <span class="reaction-timeline-marker__count">{{ reaction.count }}</span>
        </span>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import { Icon } from '@/storybook';
import { useMobileDetection } from '@/composables/useMobileDetection';
import { useReactionTimeline, ReactionTimelineMarker } from '../composables/useReactionTimeline';

// The video control bar has no responsive layout, so on narrow viewports the
// progress bar is too small for marker pills; the overlay is hidden on mobile.
const { isMobile } = useMobileDetection();
const { markers, progressHolder, seekTo } = useReactionTimeline();

const expandedBucketIndex = ref<number | null>(null);

const toggleBreakdown = (bucketIndex: number) => {
  expandedBucketIndex.value = expandedBucketIndex.value === bucketIndex ? null : bucketIndex;
};

const closeBreakdown = (bucketIndex: number) => {
  if (expandedBucketIndex.value === bucketIndex) {
    expandedBucketIndex.value = null;
  }
};

const totalCount = (marker: ReactionTimelineMarker) => marker.reactions.reduce((sum, reaction) => sum + reaction.count, 0);
</script>

<style scoped>
.reaction-timeline-marker {
  @apply absolute bottom-full mb-1.5 -translate-x-1/2 select-none;
}

.reaction-timeline-marker__pill {
  @apply flex items-center gap-1 rounded-full bg-black/70 px-2 py-1 text-sm text-white whitespace-nowrap transition-transform;
}

.reaction-timeline-marker__pill:hover,
.reaction-timeline-marker__pill:focus-within {
  @apply scale-125 bg-black/90 z-10;
}

.reaction-timeline-marker__summary {
  @apply flex items-center gap-1 cursor-pointer border-none bg-transparent p-0 focus:outline-none;
  color: inherit;
}

.reaction-timeline-marker__toggle {
  @apply flex items-center justify-center cursor-pointer border-none bg-transparent p-0 transition-transform focus:outline-none;
  color: inherit;
}

.reaction-timeline-marker__toggle--open {
  @apply rotate-180;
}

.reaction-timeline-marker__reaction {
  @apply flex items-center gap-1;
}

.reaction-timeline-marker__emoji {
  @apply text-base leading-none;
}

.reaction-timeline-marker__count {
  @apply font-semibold;
}

.reaction-timeline-marker__breakdown {
  @apply absolute bottom-full left-1/2 z-10 mb-1.5 flex -translate-x-1/2 flex-col gap-1 whitespace-nowrap rounded-lg bg-black/90 px-2 py-1.5 text-sm text-white;
}
</style>
